export type ClipKind = 'video' | 'image';

export type Layout =
  | 'single'
  | 'wipe'
  | 'side-by-side'
  | 'top-bottom'
  | 'pip'
  | 'grid'
  | 'dominant-right'
  | 'dominant-bottom';

export type PipCorner = 'br' | 'bl' | 'tr' | 'tl';

/** Animated-wipe presets: sweep speed and the direction the divider travels. */
export type WipeSpeed = 'slow' | 'med' | 'fast';
export type WipeDir = 'rl' | 'lr';

/** Sweep duration in seconds for each speed preset. */
export const WIPE_SPEED_SEC: Record<WipeSpeed, number> = { slow: 4, med: 2, fast: 1 };

export const MAX_CLIPS = 4;
export const IMAGE_DEFAULT_SEC = 10;

export interface Clip {
  id: string;
  kind: ClipKind;
  name: string;
  file: File;
  url: string;
  video: HTMLVideoElement | null;
  image: HTMLImageElement | null;
  width: number;
  height: number;
  /** Detected fps (videos only; 0 for images). */
  fps: number;
  /** Native duration in seconds (videos only; images derive theirs from the timeline). */
  durationSec: number;
  /** Timeline frame at which this clip's own frame 0 sits. Can be negative. */
  offsetFrames: number;
  opacity: number;
  thumb: string | null;
  error: string | null;
  /** WebCodecs scrub cache status (docs/SCRUBBING.md option A). */
  scrubCache: 'off' | 'building' | 'ready';
}

export interface AppState {
  clips: Clip[];
  layout: Layout;
  /** 0..1 divider position for the wipe (before/after) layout. */
  wipe: number;
  /** When on, the divider sweeps itself across the marked range (and exports). */
  wipeAnim: boolean;
  wipeSpeed: WipeSpeed;
  wipeDir: WipeDir;
  pipCorner: PipCorner;
  /** Timeline fps — the dominant (first video) clip's detected fps. */
  fps: number;
  totalFrames: number;
  frame: number;
  markIn: number;
  markOut: number;
  playing: boolean;
  exportRes: 'source' | '2160' | '1440' | '1080' | '720';
  exportFps: 'timeline' | '60' | '30' | '24' | '15';
  exporting: boolean;
}

export const state: AppState = {
  clips: [],
  layout: 'single',
  wipe: 0.5,
  wipeAnim: false,
  wipeSpeed: 'med',
  wipeDir: 'rl',
  pipCorner: 'br',
  fps: 30,
  totalFrames: 0,
  frame: 0,
  markIn: 0,
  markOut: 0,
  playing: false,
  exportRes: 'source',
  exportFps: 'timeline',
  exporting: false,
};

type Listener = () => void;
const listeners: Listener[] = [];

export function subscribe(fn: Listener): void {
  listeners.push(fn);
}

export function notify(): void {
  for (const fn of listeners) fn();
}

// ---------- derived timeline helpers ----------

/**
 * Wipe divider position (0..1) at a given timeline frame — the single source
 * of truth for both the preview and the exporter, so a recorded wipe matches
 * what playback showed.
 *
 * With the animation off this is just the divider the user dragged. With it on
 * the divider sweeps across the *marked range* (what actually exports), eased
 * in/out and centred in the range: the shot holds on the first clip, wipes,
 * then holds on the second — the before/after beat, with no keyframing.
 */
export function wipeAt(frame: number): number {
  if (!state.wipeAnim) return state.wipe;
  const start = Math.min(state.markIn, state.markOut);
  const end = Math.max(state.markIn, state.markOut);
  const rangeFrames = Math.max(1, end - start);
  // A range shorter than the preset simply sweeps for the whole range.
  const sweep = Math.max(1, Math.min(rangeFrames, WIPE_SPEED_SEC[state.wipeSpeed] * state.fps));
  const hold = (rangeFrames - sweep) / 2;
  const t = Math.min(1, Math.max(0, (frame - start - hold) / sweep));
  const eased = t * t * (3 - 2 * t); // smoothstep
  // 'rl': divider starts at the right edge (all clip 1) and travels left,
  // revealing clip 2. 'lr' is the mirror image.
  return state.wipeDir === 'lr' ? eased : 1 - eased;
}

/** Longest native video duration in seconds (image lengths derive from this). */
export function longestVideoSec(): number {
  let max = 0;
  for (const c of state.clips) {
    if (c.kind === 'video' && !c.error) max = Math.max(max, c.durationSec);
  }
  return max;
}

/** A clip's duration on the timeline: videos use their own; images track the longest video. */
export function effectiveDurationSec(clip: Clip): number {
  if (clip.kind === 'video') return clip.durationSec;
  const longest = longestVideoSec();
  return longest > 0 ? longest : IMAGE_DEFAULT_SEC;
}

/**
 * Map a timeline frame to a clip-local time in seconds, honouring the clip's
 * offset and clamping to its range (out-of-range clips hold first/last frame).
 * Seeks target the CENTER of the frame interval, not its boundary — boundary
 * times alias against the file's real sample timestamps (edit lists / audio
 * priming shift them off k/fps), making single-frame steps sometimes land on
 * the same decoded frame twice.
 */
export function clipTimeSec(clip: Clip, frame: number): number {
  const t = (frame - clip.offsetFrames + 0.5) / state.fps;
  const dur = effectiveDurationSec(clip);
  return Math.min(Math.max(t, 0), Math.max(dur - 0.5 / state.fps, 0));
}

/** True when the timeline frame falls inside the clip's own span. */
export function clipInRange(clip: Clip, frame: number): boolean {
  const t = (frame - clip.offsetFrames) / state.fps;
  return t >= 0 && t <= effectiveDurationSec(clip);
}

/**
 * Recompute fps/totalFrames after clips change (add/remove/reorder/offset).
 * Timeline fps follows the first video clip; length = longest clip incl. offset.
 * Marks pinned to the old end (or now out of range) follow the new end.
 */
export function recomputeTimeline(): void {
  const oldTotal = state.totalFrames;
  const firstVideo = state.clips.find((c) => c.kind === 'video' && !c.error);
  state.fps = firstVideo ? firstVideo.fps : 30;

  let total = 0;
  for (const c of state.clips) {
    if (c.error) continue;
    const end = c.offsetFrames + Math.round(effectiveDurationSec(c) * state.fps);
    total = Math.max(total, end);
  }
  state.totalFrames = total;

  const last = Math.max(0, total - 1);
  state.frame = Math.min(state.frame, last);
  if (state.markOut === Math.max(0, oldTotal - 1) || state.markOut > last || state.markOut === 0) {
    state.markOut = last;
  }
  state.markIn = Math.min(state.markIn, state.markOut);
}
